import { useAuthStore } from "@/stores/authStore";
import { getVeterinaire } from "@/api/auth";
import apiBase from "@/api/configBasic.js";

export class RationDataAdapter {
  constructor() {
    this.cache = new Map();
    this.authStore = useAuthStore();
  }

  /**
   * Méthode principale - Adapte les données brutes vers le format PDF uniforme
   * @param {Object} rawData - Données brutes de n'importe quelle source
   * @param {string} sourceType - Type de source ('auto', 'LISTING', 'CALCULATE', 'SAVED')
   * @returns {Promise<Object>} Données normalisées pour PDF
   */
  async adaptForPdf(rawData, sourceType = "auto") {
    try {
      const detectedType = sourceType === "auto" ? this.detectSource(rawData) : sourceType;

      let adaptedData;
      switch (detectedType) {
        case "LISTING":
          adaptedData = await this.fromListing(rawData);
          break;
        case "CALCULATE":
          adaptedData = await this.fromCalculate(rawData);
          break;
        case "SAVED":
          adaptedData = await this.fromSaved(rawData);
          break;
        default:
          throw new Error(`Type de source non supporté: ${detectedType}`);
      }

      return adaptedData;
    } catch (error) {
      console.error("Erreur lors de l'adaptation:", error);
      throw new Error(`Erreur d'adaptation PDF: ${error.message}`);
    }
  }

  /**
   * Détecte automatiquement le type de source de données
   * @param {Object} data - Données à analyser
   * @returns {string} Type détecté ('LISTING', 'CALCULATE', 'SAVED', 'UNKNOWN')
   */
  detectSource(data) {
    if (!data) return "UNKNOWN";

    if (data.animal && data.product && data.productVariant && data.recommendedDailyAmount && data.visite) {
      return "LISTING";
    }

    if (
      data.id === -1 ||
      (data.animal &&
        typeof data.animal === "object" &&
        data.owner &&
        typeof data.owner === "object" &&
        data.recommendedDailyAmount)
    ) {
      return "CALCULATE";
    }

    if (data.id && data.id > 0) {
      return "SAVED";
    }

    return "UNKNOWN";
  }

  /**
   * Adapte les données du listing (/rations/grouped-by-owners)
   * @param {Object} data - Données du listing
   * @returns {Promise<Object>} Données normalisées
   */
  async fromListing(data) {
    const veterinaryData =
      data.veterinarianAddresses && data.veterinarianAddresses.length > 0
        ? await this.formatVeterinaryFromAddresses(data.veterinarianAddresses)
        : await this.getVeterinaryData();

    const animalForPdf = {
      ...data.animal,
      idealWeight: data.idealWeight,
      estimatedAdultWeight: data.estimatedAdultWeight,
      targetWeight: data.targetWeight,
      dailyActivityName: data.dailyActivityName,
      physiologicalStageName: data.physiologicalStageName,
      weightManagementName: data.weightManagementName,
      underAge: !!data.estimatedAdultWeight,
    };

    return {
      veterinary: veterinaryData,
      animal: this.formatAnimalData(animalForPdf),
      owner: this.formatOwnerData(data.owner || {}),
      calculation: this.formatCalculationData(data),
      dates: this.formatDatesData(data),
    };
  }

  /**
   * Adapte les données du calcul temps réel (/rations/calculate)
   * @param {Object} data - Données du calcul
   * @returns {Promise<Object>} Données normalisées
   */
  async fromCalculate(data) {
    const veterinaryData =
      data.veterinarianAddresses && data.veterinarianAddresses.length > 0
        ? await this.formatVeterinaryFromAddresses(data.veterinarianAddresses)
        : await this.getVeterinaryData(data.veterinarianAddressIds || null);

    let animalData = data.animal;
    let ownerData = data.owner;

    if (typeof data.animal === "string") {
      animalData = await this.resolveAnimalReference(this.extractIdFromUrl(data.animal));
    } else if (!animalData && data.animalId) {
      animalData = await this.resolveAnimalReference(data.animalId);
    }

    if (!ownerData && data.ownerId) {
      ownerData = await this.resolveOwnerReference(data.ownerId);
    }

    const animalForPdf = {
      ...animalData,
      idealWeight: data.idealWeight,
      estimatedAdultWeight: data.estimatedAdultWeight,
      targetWeight: data.targetWeight,
      dailyActivityName: data.dailyActivityName,
      physiologicalStageName: data.physiologicalStageName,
      weightManagementName: data.weightManagementName,
    };

    return {
      veterinary: veterinaryData,
      animal: this.formatAnimalData(animalForPdf),
      owner: this.formatOwnerData(ownerData),
      calculation: this.formatCalculationData(data),
      dates: this.formatDatesData(data),
    };
  }

  /**
   * Adapte les données de ration sauvée (/shop/saved-rations)
   * @param {Object} data - Données de la ration sauvée
   * @returns {Promise<Object>} Données normalisées
   */
  async fromSaved(data) {
    const veterinaryData =
      data.veterinarianAddresses && data.veterinarianAddresses.length > 0
        ? await this.formatVeterinaryFromAddresses(data.veterinarianAddresses)
        : await this.getVeterinaryData(data.veterinarianAddressIds || null);

    const [animalData, ownerData] = await Promise.all([
      this.resolveAnimalReference(data.animalId || this.extractIdFromUrl(data.animal)),
      this.resolveOwnerReference(data.ownerId),
    ]);

    const animalForPdf = {
      ...animalData,
      idealWeight: data.idealWeight,
      estimatedAdultWeight: data.estimatedAdultWeight,
      targetWeight: data.targetWeight,
      dailyActivityName: data.dailyActivityName,
      physiologicalStageName: data.physiologicalStageName,
      weightManagementName: data.weightManagementName,
    };

    return {
      veterinary: veterinaryData,
      animal: this.formatAnimalData(animalForPdf),
      owner: this.formatOwnerData(ownerData),
      calculation: this.formatCalculationData(data),
      dates: this.formatDatesData(data),
    };
  }

  /**
   * Récupère les données vétérinaire avec cache et fallback
   * @param {Array|null} veterinarianAddressIds - IDs des adresses vétérinaires sélectionnées
   * @returns {Promise<Object>} Données vétérinaire formatées
   */
  async getVeterinaryData(veterinarianAddressIds = null) {
    const cacheKey = veterinarianAddressIds ? `veterinary_data_${veterinarianAddressIds[0]}` : "veterinary_data";
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      if (this.authStore.user?.id) {
        const response = await getVeterinaire(this.authStore.user.id);
        const customerData = response.data;
        const addressToUse = this.resolveVeterinaryAddress(veterinarianAddressIds, customerData);
        const vetData = this.formatVeterinaryFromAddress(customerData, addressToUse);

        this.cache.set(cacheKey, vetData);
        return vetData;
      }
    } catch (error) {
      console.warn("Fallback vers données store pour vétérinaire:", error.message);
    }

    const fallbackData = this.formatVeterinaryFromStore();
    this.cache.set(cacheKey, fallbackData);
    return fallbackData;
  }

  /**
   * Résout l'adresse vétérinaire à utiliser selon les IDs fournis
   * @param {Array|null} veterinarianAddressIds - IDs des adresses vétérinaires
   * @param {Object} customerData - Données customer complètes
   * @returns {Object|null} Adresse à utiliser ou null
   */
  resolveVeterinaryAddress(veterinarianAddressIds, customerData) {
    // Si pas d'adresse spécifiée, utiliser l'adresse par défaut
    if (!veterinarianAddressIds || veterinarianAddressIds.length === 0) {
      return customerData.defaultAddressData || null;
    }

    // Récupérer l'adresse spécifique depuis les adressesLivraison
    const addressId = veterinarianAddressIds[0]; // Prendre la première
    const specificAddress = customerData.adressesLivraison?.find(addr => addr.id === addressId);

    if (specificAddress) {
      return specificAddress;
    }

    // Fallback vers l'adresse par défaut
    return customerData.defaultAddressData || null;
  }

  /**
   * Formate les données vétérinaire avec une adresse spécifique
   * @param {Object} customerData - Données customer
   * @param {Object} addressData - Adresse à utiliser
   * @returns {Object} Données vétérinaire formatées
   */
  formatVeterinaryFromAddress(customerData, addressData) {
    if (!addressData) {
      // Fallback vers les données customer principales
      return {
        name: customerData.fullName || "",
        street: "",
        postalCodeCity: "",
        phone: customerData.cellphone || customerData.phoneNumber || "",
        email: customerData.email || "",
        supplyvetCode: customerData.divalto_id || "",
      };
    }

    return {
      name: addressData.fullName || customerData.fullName || "",
      street: addressData.street || "",
      postalCodeCity: this.formatPostalCodeCity(addressData.postcode, addressData.city),
      phone: addressData.cellphone || addressData.phoneNumber || customerData.cellphone || customerData.phoneNumber || "",
      email: customerData.email || "",
      supplyvetCode: customerData.divalto_id || "",
    };
  }

  /**
   * Formate les données vétérinaire depuis l'API /customers/{id}
   * @param {Object} apiData - Données de l'API
   * @returns {Object} Données vétérinaire formatées
   */
  formatVeterinaryFromApi(apiData) {
    const defaultAddress = apiData.defaultAddressData || {};

    return {
      name: apiData.fullName || apiData.lastName || "",
      street: defaultAddress.street || "",
      postalCodeCity: this.formatPostalCodeCity(defaultAddress.postcode, defaultAddress.city),
      phone: apiData.cellphone || apiData.phoneNumber || defaultAddress.cellphone || defaultAddress.phoneNumber || "",
      email: apiData.email || "",
      supplyvetCode: apiData.divalto_id || "",
    };
  }

  /**
   * Formate les données vétérinaire depuis le store (fallback)
   * @returns {Object} Données vétérinaire formatées
   */
  formatVeterinaryFromStore() {
    const user = this.authStore.user || {};
    const defaultAddress = user.defaultAddress || {};

    return {
      name: user.fullName || user.lastName || "",
      street: defaultAddress.street || "",
      postalCodeCity: this.formatPostalCodeCity(defaultAddress.postcode, defaultAddress.city),
      phone: user.cellphone || user.phoneNumber || defaultAddress.cellphone || defaultAddress.phoneNumber || "",
      email: user.email || "",
      supplyvetCode: user.divalto_id || "",
    };
  }

  /**
   * Formate les données vétérinaire depuis veterinarianAddresses
   * @param {Array} addresses - Tableau des adresses vétérinaires
   * @returns {Promise<Object>} Données vétérinaire formatées
   */
  async formatVeterinaryFromAddresses(addresses) {
    if (!addresses || addresses.length === 0) {
      return this.getEmptyVeterinaryData();
    }

    const address = addresses[0];
    const firstName = address.firstName || "";
    const lastName = address.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();

    let veterinaryData = null;
    try {
      if (this.authStore.user?.id) {
        const response = await getVeterinaire(this.authStore.user.id);
        veterinaryData = response.data;
      }
    } catch (error) {
      console.warn(`[PDF] Erreur récupération données vétérinaire pour adresse:`, error.message);
    }

    return {
      name: fullName || address.company || "",
      street: address.street || "",
      postalCodeCity: this.formatPostalCodeCity(address.postcode, address.city),
      phone: address.phoneNumber || address.cellphone || veterinaryData?.phoneNumber || "",
      email: address.email || veterinaryData?.email || "",
      supplyvetCode: veterinaryData?.divalto_id || "",
    };
  }

  /**
   * Retourne des données vétérinaire vides
   * @returns {Object} Structure vétérinaire avec chaînes vides
   */
  getEmptyVeterinaryData() {
    return {
      name: "",
      street: "",
      postalCodeCity: "",
      phone: "",
      email: "",
      supplyvetCode: "",
    };
  }

  /**
   * Formate les données animal
   * @param {Object} animal - Données animal
   * @returns {Object} Données animal formatées
   */
  formatAnimalData(animal) {
    if (!animal) return this.getEmptyAnimalData();

    let idealWeightLabel = "Poids idéal";
    let idealWeightValue = animal.idealWeight || animal.targetWeight || "";

    if (animal.underAge) {
      idealWeightLabel = "Poids adulte estimé";
      idealWeightValue = animal.estimatedAdultWeight || animal.targetWeight || "";
    }

    return {
      name: animal.name || "",
      idealWeightLabel: idealWeightLabel,
      idealWeight: idealWeightValue,
      dailyActivity: animal.dailyActivityName || animal.dailyActivity || "",
      physiologicalStage: animal.physiologicalStageName || animal.physiologicalStage || "",
      weightManagement: animal.weightManagementName || animal.weightManagement || "",
    };
  }

  /**
   * Formate les données propriétaire
   * @param {Object} owner - Données propriétaire
   * @returns {Object} Données propriétaire formatées
   */
  formatOwnerData(owner) {

    console.log(owner,"OwnerDataAdapter")
    if (!owner) return this.getEmptyOwnerData();

    return {
      fullName: this.getOwnerFullName(owner),
      email: owner.email || "",
      phone: owner.cellphone || owner.phoneNumber || "",
    };
  }

  /**
   * Formate les données de calcul
   * @param {Object} calculation - Données de calcul
   * @returns {Object} Données de calcul formatées
   */
  formatCalculationData(calculation) {
    return {
      reference: this.getProductReference(calculation),
      dailyRation: calculation.recommendedDailyAmount || "",
      dailyCost: this.getDailyCost(calculation),
      bagDuration: this.getBagDuration(calculation),
      variantImage: calculation.variantImage || null, // 🆕 Ajout du variantImage base64
      product: calculation.product || null,
      productVariant: calculation.productVariant || null,
    };
  }

  /**
   * Formate les données de dates
   * @param {Object} data - Données contenant les dates
   * @returns {Object} Données de dates formatées
   */
  formatDatesData(data) {
    return {
      visitDate: this.formatDate(data.visite),
      nextVisitDate: this.formatDate(data.nextVisite),
      reminderTypes: data.reminderJ7Type || [],
    };
  }

  /**
   * Calcule le coût journalier selon les règles métier
   * @param {Object} data - Données contenant les informations de coût
   * @returns {string} Coût formaté ou chaîne vide
   */
  getDailyCost(data) {
    if (data?.showDailyExpenses && data?.dailyExpenses) {
      const costInEuros = parseFloat(data.dailyExpenses);
      if (!isNaN(costInEuros)) {
        return `${costInEuros.toFixed(2)} €`;
      }
    }
    return "";
  }

  /**
   * Calcule la durée du sac avec fallback automatique
   * @param {Object} data - Données contenant les informations de durée
   * @returns {string} Durée formatée ou chaîne vide
   */
  getBagDuration(data) {
    if (data?.consumptionDurationDays && data.consumptionDurationDays > 0) {
      return `${data.consumptionDurationDays} jours`;
    }
    const dailyAmount = parseFloat(data?.recommendedDailyAmount || 0);
    const bagWeight = parseFloat(data?.productWeight || data?.variantWeight || 0);

    if (dailyAmount > 0 && bagWeight > 0) {
      const days = Math.round((bagWeight * 1000) / dailyAmount);
      return `${days} jours`;
    }
    return "";
  }

  /**
   * Génère la référence produit selon la source
   * @param {Object} data - Données contenant les informations produit
   * @returns {string} Référence formatée
   */
  getProductReference(data) {
    if (data.selectedProduct && data.selectedVariant) {
      const productName = data.selectedProduct.name || "";
      const variantWeight = data.selectedVariant.weight || "";
      return variantWeight ? `${productName} - ${variantWeight}kg` : productName;
    }
    if (data.product && data.productVariant && data.product.name) {
      const productName = data.product.name || "";
      const variantWeight = data.productVariant.weight || data.variantWeight || "";
      return variantWeight ? `${productName} - ${variantWeight}kg` : productName;
    }

    if (data.product || data.productVariant) {
      const productName = this.extractNameFromUrl(data.product) || "";
      const variantName = this.extractNameFromUrl(data.productVariant) || "";
      const weight = data.variantWeight || "";
      if (weight) {
        return `${productName} - ${variantName} - ${weight}kg`;
      }
      return `${productName} - ${variantName}`.trim();
    }

    return "";
  }

  /**
   * Résout une référence API animal et enrichit les données
   * @param {number} animalId - ID de l'animal
   * @returns {Promise<Object>} Données animal enrichies
   */
  async resolveAnimalReference(animalId) {
    if (!animalId) return null;

    const cacheKey = `animal_enriched_${animalId}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const animalResponse = await apiBase.get(`/api/v2/shop/animals/${animalId}`);
      const animalData = animalResponse.data;

      // Enrichissement avec les noms
      if (animalData.dailyActivityId) {
        try {
          const activityResponse = await apiBase.get(
            `/api/v2/shop/animal-daily-activities/${animalData.dailyActivityId}`
          );
          animalData.dailyActivityName = activityResponse.data.name;
        } catch (e) {
          console.warn(`Impossible de résoudre l'activité quotidienne pour l'ID ${animalData.dailyActivityId}`);
        }
      }

      if (animalData.physiologicalStageId) {
        try {
          const stageResponse = await apiBase.get(
            `/api/v2/shop/animal-physiological-stages/${animalData.physiologicalStageId}`
          );
          animalData.physiologicalStageName = stageResponse.data.name;
        } catch (e) {
          console.warn(`Impossible de résoudre le stade physiologique pour l'ID ${animalData.physiologicalStageId}`);
        }
      }

      if (animalData.weightManagementId) {
        try {
          const weightResponse = await apiBase.get(
            `/api/v2/shop/animal-weight-managements/${animalData.weightManagementId}`
          );
          animalData.weightManagementName = weightResponse.data.name;
        } catch (e) {
          console.warn(`Impossible de résoudre la gestion du poids pour l'ID ${animalData.weightManagementId}`);
        }
      }

      this.cache.set(cacheKey, animalData);
      return animalData;
    } catch (error) {
      console.error(`Erreur lors de la résolution de l'animal ${animalId}:`, error);
      return null;
    }
  }

  /**
   * Résout une référence propriétaire
   * @param {number} ownerId - ID du propriétaire
   * @returns {Promise<Object>} Données propriétaire
   */
  async resolveOwnerReference(ownerId) {
    if (!ownerId) return null;

    const cacheKey = `owner_${ownerId}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
    
      const response = await apiBase.get(`/api/v2/shop/owners/${ownerId}`);
        console.log(response.data,"owner")
      this.cache.set(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la résolution propriétaire ${ownerId}:`, error);
      return null;
    }
  }

  /**
   * Extrait l'ID depuis une URL API
   * @param {string} url - URL API (ex: "/api/v2/shop/animals/25")
   * @returns {number|null} ID extrait
   */
  extractIdFromUrl(url) {
    if (!url || typeof url !== "string") return null;
    const match = url.match(/\/(\d+)$/);
    return match ? parseInt(match[1]) : null;
  }

  /**
   * Extrait le nom depuis une URL API
   * @param {string} url - URL API
   * @returns {string} Nom extrait et décodé
   */
  extractNameFromUrl(url) {
    if (!url || typeof url !== "string") return "";
    const parts = url.split("/");
    const lastPart = parts[parts.length - 1];
    let decoded = decodeURIComponent(lastPart);
    decoded = decoded
      .replace(/%20/g, " ") // Espaces
      .replace(/%C3%89/g, "É") // É majuscule
      .replace(/%C3%A9/g, "é") // é minuscule
      .replace(/%C3%A0/g, "à") // à minuscule
      .replace(/%C3%A8/g, "è") // è minuscule
      .replace(/%C3%A7/g, "ç") // ç minuscule
      .replace(/CHIEN%20ARTICULATION/g, "CHIEN ARTICULATION")
      .replace(/CHIEN%20/g, "CHIEN ")
      .replace(/-/g, " "); // Remplacer les tirets par des espaces

    return decoded.trim();
  }

  /**
   * Formate le code postal et la ville
   * @param {string} postcode - Code postal
   * @param {string} city - Ville
   * @returns {string} Format "12345 VILLE" ou chaîne vide
   */
  formatPostalCodeCity(postcode, city) {
    const pc = postcode || "";
    const c = city || "";
    return `${pc} ${c}`.trim();
  }

  /**
   * Formate une date pour l'affichage
   * @param {string} dateString - Date au format ISO ou autre
   * @returns {string} Date formatée ou chaîne vide
   */
  formatDate(dateString) {
    if (!dateString) return "";
    try {
      return dateString.split(" ")[0]; // Garde seulement la partie date
    } catch (error) {
      return "";
    }
  }

  /**
   * Génère le nom complet du propriétaire
   * @param {Object} owner - Données propriétaire
   * @returns {string} Nom complet ou chaîne vide
   */
  getOwnerFullName(owner) {
    if (!owner) return "";

    if (owner.fullName) return owner.fullName;

    const firstName = owner.firstName || "";
    const lastName = owner.lastName || "";
    return `${firstName} ${lastName}`.trim();
  }

  /**
   * Retourne des données animal vides
   * @returns {Object} Structure animal avec chaînes vides
   */
  getEmptyAnimalData() {
    return {
      name: "",
      idealWeight: "",
      dailyActivity: "",
      physiologicalStage: "",
      weightManagement: "",
    };
  }

  /**
   * Retourne des données propriétaire vides
   * @returns {Object} Structure propriétaire avec chaînes vides
   */
  getEmptyOwnerData() {
    return {
      fullName: "",
      email: "",
      phone: "",
    };
  }

  /**
   * Vide le cache (utile pour les tests)
   */
  clearCache() {
    this.cache.clear();
    console.log("Cache RationDataAdapter vidé");
  }
}

// Export par défaut
export default RationDataAdapter;
